import React, { useCallback, useEffect, useState } from 'react';
import BarLoader from 'react-spinners/BarLoader';

import * as PrimaryService from '../services/service';
import { datadogLogs } from '../datadog';

const MODAL_ID = 'createTicketDirect';

const hideModal = () => {
    const el = document.getElementById(MODAL_ID);
    if (!el || !window.bootstrap) return;
    window.bootstrap.Modal.getInstance(el)?.hide();
};

const CreateTicketDirectModal = ({ onUnauthorized }) => {
    const [status, setStatus] = useState('loading');
    const [url, setUrl] = useState(null);
    const [error, setError] = useState(null);

    const fetchUrl = useCallback(async () => {
        setStatus('loading');
        setError(null);
        try {
            const response = await PrimaryService.getChatbotTicketUrl();
            const body = response?.data;

            if (response?.status === 200 && body?.success && body?.data?.url) {
                setUrl(body.data.url);
                setStatus('ready');
                return;
            }

            if (response?.status === 401) {
                hideModal();
                onUnauthorized?.();
                return;
            }

            setError(body?.error || 'Unable to create ticket. Please try again.');
            setStatus('error');
        } catch (err) {
            datadogLogs.logger.error('Failed to fetch chatbot ticket URL', {
                action: 'CHATBOT_TICKET_URL_ERROR',
                message: err?.message,
            });
            setError('Unable to create ticket. Please try again.');
            setStatus('error');
        }
    }, [onUnauthorized]);

    useEffect(() => {
        const el = document.getElementById(MODAL_ID);
        if (!el) return undefined;

        const onShown = () => fetchUrl();
        const onHidden = () => {
            setStatus('loading');
            setUrl(null);
            setError(null);
        };

        el.addEventListener('shown.bs.modal', onShown);
        el.addEventListener('hidden.bs.modal', onHidden);
        return () => {
            el.removeEventListener('shown.bs.modal', onShown);
            el.removeEventListener('hidden.bs.modal', onHidden);
        };
    }, [fetchUrl]);

    const openTicket = () => {
        window.open(url, '_blank', 'noopener,noreferrer');
        hideModal();
    };

    return (
        <div
            className="modal fade"
            id={MODAL_ID}
            tabIndex={-1}
            aria-labelledby={`${MODAL_ID}-label`}
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-md">
                <div className="modal-content">
                    <div className="modal-header border-0 pb-0 pt-3">
                        <button
                            type="button"
                            className="btn btn-close px-4 ms-auto"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        >
                            <img src="img/close.png" alt="Close" />
                        </button>
                    </div>

                    <div className="modal-body text-center px-5 pb-5 pt-2">
                        {status === 'loading' && (
                            <>
                                <div className="d-flex justify-content-center mb-4">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            background: '#FFF1E8',
                                            color: '#FF6307',
                                        }}
                                    >
                                        <svg
                                            aria-hidden="true"
                                            width="36"
                                            height="36"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <rect x="3" y="6" width="18" height="13" rx="2" />
                                            <path d="M3 11h18" opacity="0.4" />
                                            <circle cx="8" cy="15" r="0.5" fill="currentColor" />
                                            <circle cx="12" cy="15" r="0.5" fill="currentColor" />
                                            <circle cx="16" cy="15" r="0.5" fill="currentColor" />
                                        </svg>
                                    </div>
                                </div>
                                <h4 className="fw-bold mb-2">Preparing your ticket</h4>
                                <p className="text-muted small mb-1">
                                    We{'\u2019'}re generating a secure link to your ticket form.
                                </p>
                                <p className="text-muted small mb-4">This will only take a moment.</p>
                                <div className="d-flex justify-content-center">
                                    <BarLoader width={220} height={4} color="#FF6307" loading />
                                </div>
                            </>
                        )}

                        {status === 'ready' && url && (
                            <>
                                <div className="d-flex justify-content-center mb-3">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            background: '#E8F7EE',
                                            color: '#2EBE6A',
                                        }}
                                    >
                                        <svg
                                            aria-hidden="true"
                                            width="36"
                                            height="36"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a2 2 0 000-4V8z" />
                                            <path d="M9 12l2 2 4-4" />
                                        </svg>
                                    </div>
                                </div>
                                <h4 className="fw-bold mb-2">Support ticket ready</h4>
                                <p className=" small mb-1">
                                    Your ticket form is ready to complete.
                                </p>
                                <p className="text-muted small mb-4">
                                    You{'\u2019'}ll be taken to a secure form in a new tab.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-danger w-100 py-2 fw-semibold d-inline-flex align-items-center justify-content-center mb-3"
                                    onClick={openTicket}
                                >
                                    <span>Open your support ticket</span>
                                    <svg
                                        aria-hidden="true"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        style={{ marginLeft: 8 }}
                                    >
                                        <path
                                            d="M7 17 L17 7 M9 7 H17 V15"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                                <div
                                    className="d-flex align-items-center px-3 py-2 rounded"
                                    style={{ background: '#F5F5F7', gap: 12 }}
                                >
                                    <div
                                        className="d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            background: '#FFFFFF',
                                            color: '#5C6370',
                                        }}
                                    >
                                        <svg
                                            aria-hidden="true"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <rect x="5" y="11" width="14" height="10" rx="2" />
                                            <path d="M8 11V7a4 4 0 018 0v4" />
                                        </svg>
                                    </div>
                                    <small className="text-muted mb-0 text-start">
                                        This secure link will <strong className="text-dark">expire in a few minutes</strong>.
                                    </small>
                                </div>
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <div className="d-flex justify-content-center mb-3">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            background: '#FDECEC',
                                            color: '#D93025',
                                        }}
                                    >
                                        <svg
                                            aria-hidden="true"
                                            width="36"
                                            height="36"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 8v4" />
                                            <path d="M12 16h.01" />
                                        </svg>
                                    </div>
                                </div>
                                <h4 className="fw-bold mb-2">Unable to generate ticket</h4>
                                <p className="text-muted small mb-4">{error}</p>
                                <button
                                    type="button"
                                    className="btn btn-danger w-100 py-2 fw-semibold"
                                    onClick={fetchUrl}
                                >
                                    Try again
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateTicketDirectModal;
